<template>
    <a-modal
        :visible="true"
        width="75vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="ivm-wrap"
        @cancel="$emit('close')"
    >
        <div class="ivm-modal">
            <div class="ivm-head">
                <div>
                    <h3>Convite de fornecedores</h3>
                    <div class="ivm-sub">Convide vendedores para sua rede</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="ivm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div class="ivm-tools">
                <input ref="fileInput" type="file" accept=".xlsx" hidden @change="readFile" />
                <a-button @click="$refs.fileInput.click()">
                    <i class="fa-solid fa-file-import ivm-btn-icon"></i> Importar Excel
                </a-button>
                <a-button @click="downloadTemplate">
                    <i class="fa-solid fa-file-excel ivm-btn-icon"></i> Baixar modelo
                </a-button>
                <a-button @click="renotify">
                    <i class="fa-solid fa-bell ivm-btn-icon"></i> Reenviar notificação
                </a-button>
            </div>

            <div class="ivm-form">
                <div class="ivm-form-title">Novo convite</div>
                <a-row :gutter="[12, 12]">
                    <a-col :xs="24" :md="8" :lg="6">
                        <a-input-search
                            v-model="invitation.receiver_phone"
                            v-mask="'(##) ####-#####'"
                            placeholder="Telefone do vendedor"
                            :loading="searching"
                            enter-button
                            @search="lookup(false)"
                        />
                    </a-col>
                    <a-col :xs="24" :md="8" :lg="6">
                        <a-input
                            ref="nameInput"
                            v-model="invitation.receiver_name"
                            placeholder="Nome do vendedor"
                            :disabled="invitation.lock"
                        />
                    </a-col>
                    <a-col :xs="24" :md="8" :lg="6">
                        <a-input
                            v-model="invitation.receiver_company_name"
                            placeholder="Empresa"
                            :disabled="invitation.lock"
                        />
                    </a-col>
                    <a-col :xs="24" :lg="6" class="ivm-form-actions">
                        <a-button type="primary" :disabled="invitation.lock" :loading="sending" @click="lookup(true)">
                            <i class="fa-solid fa-paper-plane ivm-btn-icon"></i> Enviar convite
                        </a-button>
                    </a-col>
                </a-row>
                <div class="ivm-hint">
                    Busque pelo telefone: se o vendedor já estiver cadastrado, o convite é enviado na hora. Caso contrário, preencha nome e empresa e clique em Enviar convite.
                </div>
            </div>

            <div class="ivm-body">
                <a-table
                    size="small"
                    :columns="columns"
                    :data-source="invitations"
                    row-key="id"
                    :loading="loading"
                    :pagination="pagination"
                    :scroll="{ x: 'max-content' }"
                    :locale="{ emptyText: 'Nenhum convite enviado.' }"
                    @change="(p) => load(p.current)"
                >
                    <template slot="whatsapp" slot-scope="text, invite">
                        <a-tooltip :title="invite.is_valid_whatsapp == 1 ? 'Whatsapp válido' : 'Convite'">
                            <i :class="invite.is_valid_whatsapp == 1 ? 'fab fa-whatsapp ivm-whatsapp' : 'far fa-envelope ivm-muted'"></i>
                        </a-tooltip>
                    </template>
                    <template slot="status" slot-scope="text">
                        <a-tag :color="statusColor(text)">{{ translateStatus(text) }}</a-tag>
                    </template>
                    <template slot="date" slot-scope="text">{{ text | formatDate }}</template>
                    <template slot="acoes" slot-scope="text, invite">
                        <ActionMenu v-if="invite.status === 'WAITING_CONFIRMATION'" :actions="getActions(invite)" />
                    </template>
                </a-table>
            </div>

            <div class="ivm-foot">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import readXlsxFile from "read-excel-file";
import InvitationService from "@/services/v3/invitation.service";
import ProviderService from "@/services/v3/provider.service";
import QuotationService from "@/services/QuotationService";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";

const STATUS = {
    CANCELED: { label: "Cancelado", color: "red" },
    WAITING_CONFIRMATION: { label: "Aguardando Confirmação", color: "orange" },
    CONFIRMED: { label: "Confirmado", color: "green" },
};

const emptyInvitation = () => ({
    receiver_phone: "",
    receiver_name: "",
    receiver_company_name: "",
    invited_user_id: null,
    lock: true,
});

export default {
    name: "InvitationsV2Modal",
    mixins: [loaderMixin],
    components: { ActionMenu },
    data() {
        return {
            svc: new InvitationService(),
            pro_svc: new ProviderService(),
            req_svc: new QuotationService(),
            loading: false,
            searching: false,
            sending: false,
            invitations: [],
            invitation: emptyInvitation(),
            pagination: {
                current: 1,
                pageSize: 15,
                total: 0,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
            columns: [
                { title: "", key: "whatsapp", align: "center", width: 50, scopedSlots: { customRender: "whatsapp" } },
                { title: "Nome", dataIndex: "receiver_name", key: "receiver_name" },
                { title: "Telefone", dataIndex: "receiver_phone", key: "receiver_phone" },
                { title: "Empresa", dataIndex: "receiver_company_name", key: "receiver_company_name" },
                { title: "Status", dataIndex: "status", key: "status", align: "center", scopedSlots: { customRender: "status" } },
                { title: "Enviado em", dataIndex: "created_at", key: "created_at", align: "center", scopedSlots: { customRender: "date" } },
                { title: "Ações", key: "acoes", align: "center", fixed: "right", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
        };
    },
    created() {
        this.load(1);
    },
    methods: {
        translateStatus(status) {
            return STATUS[status] ? STATUS[status].label : "Não Reconhecido";
        },
        statusColor(status) {
            return STATUS[status] ? STATUS[status].color : "default";
        },
        getActions(invite) {
            return [{ label: "Cancelar convite", icon: "delete", iconClass: "action-danger", onClick: () => this.cancel(invite) }];
        },
        load(page = this.pagination.current) {
            this.loading = true;
            return this.svc
                .list({ page })
                .then(({ data }) => {
                    this.invitations = data.data || [];
                    this.pagination.total = data.total || 0;
                    this.pagination.current = page;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        lookup(sendIfNotFound) {
            this.searching = true;
            return this.pro_svc
                .find_by_phone(this.invitation.receiver_phone)
                .then(({ data }) => {
                    this.invitation = {
                        ...this.invitation,
                        receiver_name: data.usu_nome,
                        receiver_company_name: data.provider.for_nome,
                        invited_user_id: data.usu_id,
                        lock: true,
                    };
                    return this.sendInvitation();
                })
                .catch(() => {
                    this.invitation = { ...this.invitation, invited_user_id: null, lock: false };
                    if (sendIfNotFound) return this.sendInvitation();
                    this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus());
                })
                .finally(() => {
                    this.searching = false;
                });
        },
        sendInvitation() {
            this.sending = true;
            return this.svc
                .send(this.invitation)
                .then(async () => {
                    if (this.invitation.invited_user_id) {
                        await this.req_svc.enableSeller(this.invitation.invited_user_id).catch(() => null);
                    }
                    // O servidor reativa o vínculo também de quem foi convidado só pelo
                    // telefone; recarrega sempre para a lista mostrar o vendedor de volta.
                    this.$emit("load_sellers");
                    this.invitation = emptyInvitation();
                    this.snack("Convite enviado!");
                    return this.load(1);
                })
                .catch((err) => {
                    const errors = err.response && err.response.data && err.response.data.errors;
                    this.$store.dispatch("notification/add", {
                        type: "error",
                        message: (errors && errors[0]) || "Não foi possível enviar o convite.",
                    });
                })
                .finally(() => {
                    this.sending = false;
                });
        },
        cancel(invite) {
            this.confirm_action({
                message: "Confirma cancelamento do convite?",
                callback: () => {
                    this.present_loader("Cancelando convite...");
                    this.svc.cancel(invite).then(() => this.load().then(() => this.dismiss_loader()));
                },
            });
        },
        renotify() {
            this.confirm_action({
                message: "Confirma reenvio de notificações dos convites pendentes?",
                callback: () => {
                    this.present_loader("Reenviando notificações...");
                    this.svc.renotify_all().then(() => this.present_info("Notificações Enviadas!"));
                },
            });
        },
        findSellerByPhone(phone) {
            return this.pro_svc
                .find_by_phone(phone)
                .then(({ data }) => data)
                .catch(() => null);
        },
        async readFile(event) {
            const file = event.target.files ? event.target.files[0] : null;
            event.target.value = "";
            if (!file) return;
            this.present_loader("Enviando convites...");
            try {
                const rows = await readXlsxFile(file);
                for (let index = 1; index < rows.length; index++) {
                    const invitation = {
                        receiver_name: rows[index][0],
                        receiver_company_name: rows[index][1],
                        receiver_phone: rows[index][2],
                        invited_user_id: null,
                        lock: true,
                    };
                    const phoneLength = String(invitation.receiver_phone || "").length;
                    if (phoneLength !== 10 && phoneLength !== 11) continue;
                    const seller = await this.findSellerByPhone(invitation.receiver_phone);
                    if (seller) {
                        invitation.receiver_name = seller.usu_nome;
                        invitation.receiver_company_name = seller.provider.for_nome;
                        invitation.invited_user_id = seller.usu_id;
                        this.req_svc.enableSeller(seller.usu_id);
                    }
                    await this.svc.send(invitation).catch(() => null);
                }
                this.$emit("load_sellers");
                this.present_info("Convites enviados!");
                this.load(1);
            } catch (error) {
                this.dismiss_loader();
                ErrorHandlerService.handle(error, this.$store);
            }
        },
        downloadTemplate() {
            const link = document.createElement("a");
            link.href = "/importacao-fornecedores.xlsx";
            link.download = "modelo-importacao-fornecedores.xlsx";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    },
};
</script>

<style lang="scss" scoped>
.ivm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.ivm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.ivm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.ivm-x.ant-btn,
.ivm-x.ant-btn:focus,
.ivm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ivm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.ivm-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.ivm-btn-icon {
    margin-right: 6px;
}
.ivm-form {
    margin: 0 20px 12px;
    padding: 12px 14px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
}
.ivm-form-title {
    font-weight: 600;
    font-size: 13px;
    color: #262626;
    margin-bottom: 8px;
}
.ivm-form-actions {
    display: flex;
    justify-content: flex-end;
}
.ivm-hint {
    margin-top: 8px;
    font-size: 12px;
    color: #8c8c8c;
}
.ivm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.ivm-whatsapp {
    color: #30aa4c;
    font-size: 1.1rem;
}
.ivm-muted {
    color: #8c8c8c;
}
.ivm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.ivm-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
